import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { store } from "./redux/store.js";
import { lazy, useState, useEffect, useRef, Suspense } from "react";
import Header from "./components/header/header";
import Footer from "./components/footer/footer";
import ScrollToTop from "./components/scrollTop";
import LoadingBar from "react-top-loading-bar";
import PageNotFound from "./components/PageNotFound";
import ErrorBoundary from "./components/ErrorBoundary";
import Preloader from "./Preloader";
import { PreloaderProvider } from "./PreloaderContext";
import "../src/assets/scss/main-style.scss";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { partnerDisplay } from "./components/constants/partnerList.js";
import { isTokenExpired } from "./service/helpers";
import { callToLoadAnalytics } from "./service/reports.js";
import { fetchLogin } from "./redux/slices/login.js";
import AnalyticsProvider from "./components/AnalyticsProvider";
import { analytics } from "./analytics.js";
import { PATHNAMES } from "./components/constants/pathNameConstants";

const Home = lazy(() => import("./Pages/Home/Vehiclehistory"));
const VHR_viewFullReports = lazy(
  () => import("./components/vehiclehistory/VHR_viewFullReports"),
);

// Standalone Screens
const SubscriptionBenefits = lazy(
  () => import("./components/vehiclehistory/subscription-benefits"),
);
const VehicleHistoryReports = lazy(
  () => import("./components/vehiclehistory/vehicle-history-reports"),
);
const SampleVehicleHistoryReports = lazy(
  () => import("./components/vehiclehistory/sample-vehicle-history-reports"),
);
const SearchByLicensePlate = lazy(
  () => import("./components/vehiclehistory/search-by-license-plate"),
);
const AutoshoppersGuide = lazy(
  () => import("./components/vehiclehistory/autoshoppers-guide"),
);
const VinBasics = lazy(() => import("./components/vehiclehistory/vin-basics"));
const VinBasicsDecode = lazy(
  () => import("./components/vehiclehistory/vin-basics"),
);
const VinBasicsFindVin = lazy(
  () => import("./components/vehiclehistory/vin-basics"),
);
const OdometerFraud = lazy(
  () => import("./components/vehiclehistory/odometer-fraud"),
);
const CallForAction = lazy(
  () => import("./components/vehiclehistory/call-for-action"),
);
const TitleBrands = lazy(
  () => import("./components/vehiclehistory/title-brands"),
);
const Faq = lazy(() => import("./components/vehiclehistory/faq"));
const Contact = lazy(() => import("./components/vehiclehistory/contact"));
const Dispute = lazy(() => import("./components/vehiclehistory/dispute"));
const Checkout = lazy(() => import("./components/vehiclehistory/checkout"));
const FreeOnlineDelivery = lazy(
  () => import("./components/vehiclehistory/free-online-delivery"),
);
const TermsOfUse = lazy(() => import("./components/TermsOfUse"));
const SecureShopping = lazy(
  () => import("./components/vehiclehistory/secure-shopping"),
);
const Glossary = lazy(() => import("./components/vehiclehistory/glossary"));
const AutoCheckScore = lazy(
  () => import("./components/vehiclehistory/autocheck-score"),
);
const PatentNotice = lazy(
  () => import("./components/vehiclehistory/patent-notice"),
);
const VehicleBuybackProtection = lazy(
  () => import("./components/vehiclehistory/vehicle-buyback-protection"),
);
const BackedByExperian = lazy(
  () => import("./components/vehiclehistory/backed-by-experian"),
);
const AutoCheckVsCarfax = lazy(
  () => import("./components/vehiclehistory/autocheck-vs-carfax"),
);
const AutoCheckVsBargain = lazy(
  () => import("./components/vehiclehistory/autocheck-vs-bargain"),
);
const FloodRiskCheck = lazy(
  () => import("./components/vehiclehistory/flood-risk-check"),
);
const AffiliateProgram = lazy(
  () => import("./components/vehiclehistory/affiliate-program"),
);
const Login = lazy(() => import("./components/vehiclehistory/login"));
const BuybackTerms = lazy(
  () => import("./components/vehiclehistory/buyback-terms"),
);
const CarGurusUser = lazy(
  () => import("./components/vehiclehistory/car-gurus-user"),
);

const CarsUser = lazy(() => import("./components/vehiclehistory/cars-user"));
const FreeScore = lazy(() => import("./components/vehiclehistory/free-score"));
const MYVehicleOffers = lazy(
  () => import("./components/vehiclehistory/my-vehicle-offers"),
);
const PrefrencesUpdated = lazy(() => import("./components/PrefrencesUpdated"));

const RecallCheck = lazy(
  () => import("./components/vehiclehistory/recall-check"),
);

const CarAdvise = lazy(() => import("./components/vehiclehistory/car-advise"));

const WhyBuyvhr = lazy(() => import("./components/vehiclehistory/why-buy-vhr"));

const TrueCar8900LandingPage = lazy(
  () => import("./Pages/TrueCar/TrueCar8900"),
);

// const PaymentGateway = lazy(
//   () => import("./components/vehiclehistory/payment-gateway"),
// );

const MyAutocheckDashboard = lazy(
  () => import("./components/mydashboard/my-autocheck-dashboard"),
);
const MyReportTab = lazy(
  () => import("./components/mydashboard/my-report-tab"),
);
const MyProfileTab = lazy(
  () => import("./components/mydashboard/my-profile-tab"),
);
const RegisterBuybackTab = lazy(
  () => import("./components/mydashboard/register-for-buyback-protection-tab"),
);
const UpdatePassword = lazy(
  () => import("./components/mydashboard/update-password"),
);

const YourAdChoices = lazy(() => import("./components/yourAdChoices"));

function AppContent() {
  const [progress, setProgress] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { partnerSiteId } = useSelector((state) => state.authentication);
  const initializedRef = useRef(false);
  const analyticsLoadedRef = useRef(false);

  useEffect(() => {
    // Check URL for siteID parameter (case-insensitive)
    const urlParams = new URLSearchParams(location.search);
    const urlSiteId =
      urlParams.get("siteID") ||
      urlParams.get("siteid") ||
      urlParams.get("siteId") ||
      urlParams.get("SITEID");

    console.log("🔍 App.js - URL siteID:", urlSiteId);
    console.log("🔍 App.js - partnerSiteId from Redux:", partnerSiteId);
    console.log("🔍 App.js - initializedRef:", initializedRef.current);

    // Only dispatch API call if siteID is explicitly provided in URL
    if (urlSiteId) {
      console.log("✅ Dispatching with URL siteID:", urlSiteId);
      dispatch(
        fetchLogin({
          condition: "partnerSiteId",
          payload: parseInt(urlSiteId),
        }),
      );
      initializedRef.current = true;
    } else if (
      !initializedRef.current &&
      (!partnerSiteId || !partnerSiteId?.uid)
    ) {
      // If not yet initialized and Redux state is empty or has no uid, check sessionStorage and populate Redux
      const storedPartnerSiteId = JSON.parse(
        sessionStorage.getItem("partnerSiteId"),
      );
      console.log("📦 SessionStorage partnerSiteId:", storedPartnerSiteId);
      if (storedPartnerSiteId?.siteId !== undefined) {
        console.log(
          "✅ Dispatching with sessionStorage siteID:",
          storedPartnerSiteId.siteId,
        );
        dispatch(
          fetchLogin({
            condition: "partnerSiteId",
            payload: storedPartnerSiteId.siteId,
          }),
        );
        initializedRef.current = true;
      }
    } else {
      console.log(
        "ℹ️ Skipping dispatch - Redux already has data or initialized",
      );
    }
    // If Redux state already has data and no URL siteID, do nothing - rely on existing state
  }, [location.search, dispatch]);

  // Load analytics configuration (ONCE per session)
  useEffect(() => {
    if (analyticsLoadedRef.current) {
      console.debug(
        "⏭️ Analytics already loaded, skipping (saved reCAPTCHA call)",
      );
      return;
    }

    analyticsLoadedRef.current = true;

    // CRITICAL FIX: Conditionally load analytics based on response (bot detection)
    // Matches legacy Vue.js behavior EXACTLY
    callToLoadAnalytics()
      .then((response) => {
        // response === true means reCAPTCHA challenge was passed (not a bot)
        if (response === true) {
          console.log(
            "✅ Analytics authorized - initializing analytics (user verified)",
          );
          // MATCHES LEGACY: loadAnalytics() - Initialize analytics ONLY after bot check passes
          analytics.initialize();
        } else {
          console.warn(
            "⚠️ Analytics NOT loaded - bot detected or unauthorized:",
            response,
          );
          // Block analytics from loading
          window.__DISABLE_ANALYTICS = true;
        }
      })
      .catch((error) => {
        console.warn("⚠️ Analytics configuration failed to load:", error);
        // MATCHES LEGACY: Fallback - Load analytics anyway on error (fail-open)
        console.log("📊 Loading analytics as fallback despite error");
        analytics.initialize();
        analyticsLoadedRef.current = false; // Reset on error to allow retry
      });
  }, []);

  useEffect(() => {
    setProgress(30);
    setTimeout(() => setProgress(100), 500);
  }, [location]);

  const titleMap = {
    [PATHNAMES.VEHICLEHISTORY_HOME]: "AutoCheck.com | Homepage | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_SAMPLE_REPORT]:
      "Sample VehicleHistory Report | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_AUTOSHOPPERS_GUIDE]:
      "How to Buy a Used Car - An Auto Shopper's Guide | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_BACKED_BY_EXPERIAN]:
      "Data Backed by Experian | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_AUTOCHECK_SCORE]:
      "AutoCheck Score | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_BUYBACK_PROTECTION]:
      "Buyback Protection | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_SUBSCRIPTION_BENEFITS]:
      "SubscriptionBenefits | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_FLOOD_RISK_CHECK]:
      "AutoCheck Flood Risk Check | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_DISPUTE]: "Dispute | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_CONTACT]: "Contact | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_LOGIN]: "Login | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_SEARCH_BY_LICENSE_PLATE]:
      "Vehicle History Report by License Plate Number | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_VIN_BASICS]:
      "What is a VIN (Vehicle Identification Number)?| AutoCheck | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_ODOMETER_FRAUD]:
      "Car Odometer Rollback Fraud | AutoCheck | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_CALL_FOR_ACTION]:
      "Consumer Advocacy: Our partnership with Call for Action | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_TITLE_BRANDS]:
      "Car Branded Titles: Salvage, Lemon, Odometer Rollback| AutoCheck | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_FAQ]:
      "Frequently Asked Questions | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_FREE_ONLINE_DELIVERY]:
      "Free Online Vehicle History Report Delivery | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_SECURE_SHOPPING]:
      "Secure Shopping | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_BUYBACK_TERMS]:
      "Buyback Terms | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_GLOSSARY]:
      "Glossary for Autoshoppers | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_PATENT_NOTICE]:
      "Patent Notice | AutoCheck.com | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_AUTOCHECK_VS_CARFAX]:
      "AutoCheck vs. Carfax | AutoCheck.com | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_AUTOCHECK_VS_BARGAIN]:
      "AutoCheck v Bargain Reports | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_AFFILIATE_PROGRAM]:
      "Become Affiliate | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_DASHBOARD]: "Dashboard | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_DASHBOARD_PROFILE]: "Profile | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_REPORTS]:
      "AutoCheck vehicle history reports | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_YOUR_AD_CHOICES]:
      "Your Ad Choices | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_CHECKOUT]: "Checkout | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_CAR_GURUS]:
      "CarGurus Landing Page | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_FREE_SCORE]:
      "Free Vehicle History Report Score | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_MY_VEHICLE_OFFERS]:
      "My Vehicle Offers | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_RECALL_CHECK]:
      "Does My Vehicle Have Any Recall Notices?",
    [PATHNAMES.VEHICLEHISTORY_CAR_ADVISE]:
      "CarAdvise | AutoCheck.com | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_TRUECAR]: "TrueCar Special Offer | AutoCheck.com",
    [PATHNAMES.VEHICLEHISTORY_TRUECAR_8900]:
      "TrueCar Special Offer | AutoCheck.com",
  };

  useEffect(() => {
    if (location.pathname.startsWith(PATHNAMES.FULL_VEHICLE_REPORT_PREFIX)) {
      document.title = "Full Vehicle Report | AutoCheck.com";
      return;
    }

    // Check static routes
    const newTitle = titleMap[location.pathname];
    if (newTitle) {
      document.title = newTitle;
    }
  }, [location.pathname]);

  const handleLoaderFinished = () => {
    setProgress(0);
  };

  // const isTokenExpired = () => {
  //   const token = localStorage.getItem("consumerSite_token");
  //   if (token) {
  //     const { exp } = jwtDecode(token);
  //     const expirationTime = exp * 1000 - 60000;
  //     const currentTime = Date.now();
  //     return currentTime >= expirationTime;
  //   } else {
  //     return false;
  //   }
  // };

  useEffect(() => {
    if (isTokenExpired() === true) {
      localStorage.clear();
      navigate(PATHNAMES.VEHICLEHISTORY_LOGIN, {});
    }
  }, [window.location.href]);

  return (
    <>
      <LoadingBar
        color="#004990"
        progress={progress}
        onLoaderFinished={handleLoaderFinished}
      />
      <Header />
      {location.pathname !== PATHNAMES.VEHICLEHISTORY_MY_VEHICLE_OFFERS &&
        partnerDisplay?.map((val) => {
          if (val.headerPanel !== undefined) {
            if (val?.id === partnerSiteId?.uid) {
              return (
                <div className="container" key={val.id}>
                  <div className="row">
                    <div className="col-12 mt-3 mb-3">
                      <div className="row d-flex align-items-center partnerHeaderPanel">
                        <div className="partnerName col-7">
                          {val.headerPanel !== undefined && (
                            <>
                              <h3>
                                Welcome to AutoCheck
                                <sup>®</sup>
                              </h3>
                            </>
                          )}

                          <span>{val?.headerPanel?.title} </span>
                        </div>

                        {/* Render the logo only if it exists */}
                        {val?.headerPanel?.logo && (
                          <div className="partnerLogo col-5">
                            <img
                              src={val?.headerPanel?.logo}
                              alt="Partner logo"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
          }

          return null;
        })}
      <ScrollToTop />
      <main>
        <Suspense fallback={<Preloader />}>
          <Routes>
            <Route path={PATHNAMES.ROOT} element={<Home />} />
            <Route path={PATHNAMES.VEHICLEHISTORY_HOME} element={<Home />} />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_TRUECAR}
              element={<TrueCar8900LandingPage />}
            />
            {/* <Route path={PATHNAMES.VEHICLEHISTORY_HOME} element={<Home />} /> */}
            <Route path={PATHNAMES.WILDCARD} element={<PageNotFound />} />
            <Route path={PATHNAMES.VEHICLEHISTORY_LOGIN} element={<Login />} />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_SUBSCRIPTION_BENEFITS}
              element={<SubscriptionBenefits />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_SEARCH_BY_LICENSE_PLATE}
              element={<SearchByLicensePlate />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_TERMS_OF_USE}
              element={<TermsOfUse />}
            />

            <Route
              path={PATHNAMES.VEHICLEHISTORY_REPORTS}
              element={<VehicleHistoryReports />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_SAMPLE_REPORT}
              element={<SampleVehicleHistoryReports />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_AUTOSHOPPERS_GUIDE}
              element={<AutoshoppersGuide />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_VIN_BASICS}
              element={<VinBasics />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_VIN_BASICS_DECODE}
              element={<VinBasicsDecode />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_VIN_BASICS_FINDVIN}
              element={<VinBasicsFindVin />}
            />
            <Route
              path={PATHNAMES.FULL_VEHICLE_REPORT_WITH_OWNER}
              element={<VHR_viewFullReports />}
            />
            <Route
              path={PATHNAMES.FULL_VEHICLE_REPORT_WITHOUT_OWNER}
              element={<VHR_viewFullReports />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_WHY_BUY_VHR}
              element={<WhyBuyvhr />}
            />

            <Route
              path={PATHNAMES.VEHICLEHISTORY_ODOMETER_FRAUD}
              element={<OdometerFraud />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_CALL_FOR_ACTION}
              element={<CallForAction />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_TITLE_BRANDS}
              element={<TitleBrands />}
            />
            <Route path={PATHNAMES.VEHICLEHISTORY_FAQ} element={<Faq />} />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_BUYBACK_TERMS}
              element={<BuybackTerms />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_CONTACT}
              element={<Contact />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_DISPUTE}
              element={<Dispute />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_FREE_ONLINE_DELIVERY}
              element={<FreeOnlineDelivery />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_SECURE_SHOPPING}
              element={<SecureShopping />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_GLOSSARY}
              element={<Glossary />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_AUTOCHECK_SCORE}
              element={<AutoCheckScore />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_PATENT_NOTICE}
              element={<PatentNotice />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_BUYBACK_PROTECTION}
              element={<VehicleBuybackProtection />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_CAR_GURUS}
              element={<CarGurusUser />}
            />

            <Route
              path={PATHNAMES.VEHICLEHISTORY_FREE_SCORE}
              element={<FreeScore />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_MY_VEHICLE_OFFERS}
              element={<MYVehicleOffers />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_PREFERENCES_UPDATED}
              element={<PrefrencesUpdated />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_RECALL_CHECK}
              element={<RecallCheck />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_CAR_ADVISE}
              element={<CarAdvise />}
            />

            <Route
              path={PATHNAMES.VEHICLEHISTORY_CARSCOM}
              element={<CarsUser />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_BACKED_BY_EXPERIAN}
              element={<BackedByExperian />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_AUTOCHECK_VS_CARFAX}
              element={<AutoCheckVsCarfax />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_AUTOCHECK_VS_BARGAIN}
              element={<AutoCheckVsBargain />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_FLOOD_RISK_CHECK}
              element={<FloodRiskCheck />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_AFFILIATE_PROGRAM}
              element={<AffiliateProgram />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_CHECKOUT}
              element={<Checkout />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_CHECKOUT_ORDER_CONFIRMATION}
              element={<Checkout />}
            />

            {/* <Route
              path={PATHNAMES.VEHICLEHISTORY_PAYMENT_GATEWAY}
              element={<PaymentGateway />}
            /> */}

            <Route
              path={PATHNAMES.VEHICLEHISTORY_DASHBOARD}
              element={<MyAutocheckDashboard />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_DASHBOARD_PROFILE}
              element={<MyProfileTab />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_DASHBOARD_REPORT}
              element={<MyReportTab />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_UPDATE_PASSWORD}
              element={<UpdatePassword />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_DASHBOARD_REGISTER_BUYBACK}
              element={<RegisterBuybackTab />}
            />
            <Route
              path={PATHNAMES.VEHICLEHISTORY_YOUR_AD_CHOICES}
              element={<YourAdChoices />}
            />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="app_container">
      <Provider store={store}>
        <PreloaderProvider>
          <BrowserRouter>
            <AnalyticsProvider>
              <ErrorBoundary>
                <AppContent />
              </ErrorBoundary>
            </AnalyticsProvider>
          </BrowserRouter>
        </PreloaderProvider>
      </Provider>
    </div>
  );
}

export default App;
